import { useCallback, useEffect, useRef } from 'react';

/** Pixels per second — tuned for smooth, readable motion */
const PX_PER_SEC = 92;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Horizontal track: hover right edge → scroll advances (content moves left, next reviews appear).
 * Hover left edge → scroll reverses. Uses rAF for smooth motion vs setInterval jumps.
 */
export function useReviewsHoverMarquee(enabled: boolean) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dirRef = useRef<-1 | 0 | 1>(0);
  const rafRef = useRef<number | null>(null);
  const lastTRef = useRef<number | null>(null);

  const stopHoverScroll = useCallback(() => {
    dirRef.current = 0;
    lastTRef.current = null;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startHoverScroll = useCallback(
    (edge: 'left' | 'right') => {
      if (!enabled || prefersReducedMotion()) return;
      // Right zone: advance scrollLeft → strip moves left, user sees “next” cards.
      // Left zone: decrease scrollLeft → strip moves right.
      dirRef.current = edge === 'right' ? 1 : -1;
      if (rafRef.current != null) return;

      const tick = (t: number) => {
        const d = dirRef.current;
        if (d === 0) {
          rafRef.current = null;
          lastTRef.current = null;
          return;
        }
        const el = trackRef.current;
        const last = lastTRef.current ?? t;
        lastTRef.current = t;
        const dt = Math.min((t - last) / 1000, 0.072);
        if (el && dt > 0) {
          el.scrollLeft += d * PX_PER_SEC * dt;
          const max = el.scrollWidth - el.clientWidth;
          if (max > 0) {
            /* Reviews track renders duplicated cards [A..Z][A..Z].
               Wrap by one logical cycle (half scrollWidth) for seamless loop. */
            const cycle = Math.max(1, Math.floor(el.scrollWidth / 2));
            if (el.scrollLeft >= cycle) el.scrollLeft -= cycle;
            else if (el.scrollLeft <= 0.5) el.scrollLeft += cycle;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      lastTRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    },
    [enabled],
  );

  useEffect(() => () => stopHoverScroll(), [stopHoverScroll]);

  return { trackRef, startHoverScroll, stopHoverScroll };
}
